import {useEffect, useState} from "react";
import {Link, useNavigate} from "react-router-dom";
import Switch from "@mui/material/Switch";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import CoverLayout from "layouts/authentication/components/CoverLayout";
import logo from "assets/images/logo.png";
import mytrustie from "assets/images/mytrustie.png";
import mytrustieLogo from "assets/images/mytrustie_icon.png";
import {setAuthentication, setLoggedInUserDetail, useRayzayUIController} from "../../../context";
import {login} from "../../../api/auth/authApiClient";
import SoftAlert from "../../../components/Alert";
import {getAllUserByUserName} from "../../../api/user/userApiClient";

const pulseDotStyle = `
  @keyframes pulseDot {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
  }
  .animate-pulse-dot {
    animation: pulseDot 1s ease-in-out infinite;
  }
`;

const floatStyle = `
  @keyframes floatY {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-10px); }
  }
  .hero-float {
    animation: floatY 2s ease-in-out infinite;
  }
`;

function SignIn() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [controller, dispatch] = useRayzayUIController();
  const { isAuthenticated } = controller;
  const [rememberMe, setRememberMe] = useState(true);
  const handleSetRememberMe = () => setRememberMe(!rememberMe);
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = async () => {
    try {
      const loginResult = await login(username, password);
      if (loginResult && loginResult.token) {
        setAuthentication(dispatch, true);
        const userDetail = await getAllUserByUserName(username);
        setLoggedInUserDetail(dispatch, userDetail.data);

          // Check if password change is required
          if (loginResult.passwordChangeRequired) {
              navigate("/change-password");
          } else {
              navigate("/dashboard");
          }
      } else {
        throw new Error("Login failed: No token returned");
      }
    } catch (err) {
      setError(err?.message || 'Login failed');
      setShowAlert(true);
    }
  };


  return (

      <CoverLayout
          header={
            <SoftBox textAlign="left" mb={2}>
              <SoftBox
                component="img"
                src={logo}
                alt="Mytrustie"
                className="hero-float"
                sx={{
                  width: "100%",
                  maxWidth: { xs: 220, sm: 280 },
                  height: "auto",
                  mx: "auto",
                  mb: 2,
                  display: "block",
                }}
              />
              <SoftBox display="flex" alignItems="center" mb={1} ml={0.5}>
                <SoftBox
                  component="span"
                  className="animate-pulse-dot"
                  sx={{
                    display: "inline-block",
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: "#d32f2f",
                    mr: 1,
                  }}
                />
                <SoftTypography variant="caption" fontWeight="bold" color="text">
                  LIVE
                </SoftTypography>
              </SoftBox>
              <SoftTypography variant="h3" fontWeight="bold" color="primary" textGradient>
                Welcome back
              </SoftTypography>
              <SoftTypography variant="body2" fontWeight="regular" color="text">
                Enter your email and password to sign in
              </SoftTypography>
            </SoftBox>
          }
      >
        <style>{pulseDotStyle}</style>
        <style>{floatStyle}</style>
        {error && (
            <SoftAlert
                color="error"
                dismissible={false}
                timeout={10}
                mount={showAlert}
                onClose={() => {
                  setError(null);
                  setShowAlert(false); // Reset alert display
                }}
            >
              {error}
            </SoftAlert>
        )}
        <SoftBox component="form" role="form">
          <SoftBox mb={2}>
            <SoftBox mb={1} ml={0.5}>
              <SoftTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
              >
                Email
              </SoftTypography>
            </SoftBox>
            <SoftInput
                type="email"
                placeholder="Email"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
            />
          </SoftBox>
          <SoftBox mb={2}>
            <SoftBox mb={1} ml={0.5}>
              <SoftTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold">
                  Password
              </SoftTypography>
            </SoftBox>
            <SoftInput type="password"
                       placeholder="Password"
                       value={password}
                       onChange={(e) => setPassword(e.target.value)}
            />
          </SoftBox>
          <SoftBox display="flex" alignItems="center">
            <Switch checked={rememberMe} onChange={handleSetRememberMe} />
            <SoftTypography
                variant="button"
                fontWeight="regular"
                onClick={handleSetRememberMe}
                sx={{ cursor: "pointer", userSelect: "none" }}
            >
              &nbsp;&nbsp;Remember me
            </SoftTypography>
          </SoftBox>
          <SoftBox mt={4} mb={1}>
            <SoftButton
                variant="gradient"
                color="primary"
                fullWidth
                onClick={() => handleLogin()}
            >
              sign in
            </SoftButton>
          </SoftBox>
          <SoftBox mt={1} textAlign="center">
            <SoftTypography variant="button" color="text" fontWeight="regular">
              <SoftTypography
                component={Link}
                to="/authentication/reset-password"
                variant="button"
                color="info"
                fontWeight="medium"
                textGradient
                sx={{
                  cursor: "pointer",
                }}
              >
                Forgot your password?
              </SoftTypography>
            </SoftTypography>
          </SoftBox>
        </SoftBox>
      </CoverLayout>
  );
}

export default SignIn;
